import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Clock, Tag, Newspaper } from 'lucide-react';
import api from '../../services/api';

const cardStyles = [
  {
    tagColor: 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-400/10 border-blue-200 dark:border-blue-400/20',
    cardBg: 'bg-blue-50/80 dark:bg-[#1A2A4A]',
    cardBorder: 'border-blue-100 dark:border-blue-500/20',
    cardGlow: 'hover:border-blue-300 dark:hover:border-blue-400/40',
  },
  {
    tagColor: 'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-400/10 border-amber-200 dark:border-amber-400/20',
    cardBg: 'bg-amber-50/80 dark:bg-[#1A2A4A]',
    cardBorder: 'border-amber-100 dark:border-amber-500/20',
    cardGlow: 'hover:border-amber-300 dark:hover:border-amber-400/40',
  },
  {
    tagColor: 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-400/10 border-green-200 dark:border-green-400/20',
    cardBg: 'bg-green-50/80 dark:bg-[#1A2A4A]',
    cardBorder: 'border-green-100 dark:border-green-500/20',
    cardGlow: 'hover:border-green-300 dark:hover:border-green-400/40',
  },
];

const fallbackPosts = [
  {
    slug: null,
    title: 'New MSME Definition 2024: What It Means for Your Business',
    excerpt: 'The government updated MSME investment & turnover thresholds. Here\'s how the new definition impacts loan eligibility and benefits.',
    tag: 'Policy Update',
    readTime: '5 min read',
  },
  {
    slug: null,
    title: '10 Tax-Saving Strategies Every MSME Should Know in FY 2025-26',
    excerpt: 'From Section 80JJAA deductions to presumptive taxation schemes – a comprehensive guide to legally minimizing your tax burden.',
    tag: 'Tax Tips',
    readTime: '8 min read',
  },
  {
    slug: null,
    title: 'How to Get a ₹50 Lakh Business Loan in 48 Hours',
    excerpt: 'Step-by-step guide to preparing your documents, choosing the right lender, and getting quick approval for your MSME business loan.',
    tag: 'Business Finance',
    readTime: '6 min read',
  },
];

function excerptFromContent(content) {
  const plain = (content || '').replace(/<[^>]*>/g, ' ').replace(/[#*_`>-]/g, ' ').replace(/\s+/g, ' ').trim();
  return plain.length > 140 ? plain.slice(0, 140).trim() + '…' : plain;
}

function readTimeFromContent(content) {
  const words = (content || '').trim().split(/\s+/).filter(Boolean).length;
  return `${Math.max(1, Math.round(words / 200))} min read`;
}

export function BlogPreview() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState(fallbackPosts);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const res = await api.get('/api/blog');
        const data = Array.isArray(res.data) ? res.data : (res.data?.blogs || []);
        if (data.length > 0) {
          setPosts(data.slice(0, 3).map((post) => ({
            slug: post.slug,
            title: post.title,
            excerpt: excerptFromContent(post.content),
            tag: post.tags?.[0] || 'Business Update',
            readTime: readTimeFromContent(post.content),
          })));
        }
      } catch {
        // keep fallback
      }
    };
    fetchPosts();
  }, []);

  return (
    <section className="py-24 bg-gradient-to-b from-gray-50 to-gray-100 dark:from-[#0A1628] dark:to-[#0d1e3a] relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex items-end justify-between mb-12 flex-wrap gap-4"
        >
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-rose-500/10 border border-rose-500/20 text-rose-600 dark:text-rose-400 text-sm font-bold mb-4">
              <Newspaper className="w-4 h-4" /> Insights
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-slate-900 dark:text-white font-heading">Latest Business Insights</h2>
            <p className="text-slate-600 dark:text-slate-400 mt-3 text-lg">Expert advice for growing Indian businesses.</p>
          </div>
          <motion.button
            whileHover={{ scale: 1.05 }}
            onClick={() => navigate('/blog')}
            className="flex items-center gap-2 px-6 py-3 rounded-full border border-gray-300 dark:border-[#2A3F5F] text-gray-700 dark:text-white/70 hover:text-slate-900 dark:text-white hover:border-white/40 text-sm font-bold transition-all"
          >
            View All Articles <ArrowRight className="w-4 h-4" />
          </motion.button>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6">
          {posts.map((post, i) => {
            const style = cardStyles[i % cardStyles.length];
            return (
              <motion.article
                key={post.slug || post.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.12, duration: 0.5 }}
                whileHover={{ y: -6, scale: 1.02 }}
                onClick={() => navigate(post.slug ? `/blog/${post.slug}` : '/blog')}
                className={`group ${style.cardBg} border ${style.cardBorder} ${style.cardGlow} rounded-2xl p-6 cursor-pointer hover:shadow-xl dark:hover:shadow-2xl transition-all duration-300 shadow-sm dark:shadow-lg flex flex-col`}
              >
                <div className="flex items-center justify-between mb-4">
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border ${style.tagColor}`}>
                    <Tag className="w-3 h-3" />
                    {post.tag}
                  </span>
                  <span className="text-slate-500 dark:text-slate-400 text-xs flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {post.readTime}
                  </span>
                </div>
                <h3 className="text-slate-900 dark:text-white font-bold text-lg leading-tight mb-3 group-hover:text-blue-600 dark:group-hover:text-cyan-300 transition-colors font-heading">
                  {post.title}
                </h3>
                <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed flex-1">{post.excerpt}</p>
                <div className="mt-5 flex items-center gap-1.5 text-blue-600 dark:text-cyan-400 text-sm font-bold group-hover:gap-3 transition-all">
                  Read More <ArrowRight className="w-4 h-4" />
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
